import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SparkIcon } from '../../../assets/icons';
import { AnalyticsEvent, captureAnalyticsEvent } from '../../../constant/Events';
import { useAllowedFeatures } from '../../../hooks/useFeaturePersonalization';
import { useOpenSparkChat } from '../../../hooks/useOpenSparkChat';
import { useFetchChargerMessageBannersQuery } from '../../../services/charger.api';
import { Charger } from '../../../stores/types';
import { SparkBannerCallToAction } from '../../../stores/types/inlineMessageBanner.interface';
import { SparkChatTrigger } from '../../../stores/types/sparkChat.interface';
import { InlineMessage } from '../../_ui/InlineMessage.component';
import { SparkChatWidget } from '../SparkChatWidget.component';

interface IProps {
  charger: Charger;
  /** Switches the surrounding panel to the Charger History tab (used by the `charger_history` CTA). */
  onViewChargerHistory: () => void;
}

const BANNER_DISMISS_DURATION_MS = (12 * 60 + 1) * 60 * 1000;

/** `interaction_mode.trigger` reported to Spark AI for this entry point. */
const BANNER_TRIGGER: SparkChatTrigger = 'notification-banner';

// Isolated widget ids so the banner's "Learn more" Spark button stays separate
// from the charger-summary "Analyze" button (which uses the default ids). This
// keeps their params independent: the banner opens the chat with its lookback
// range, the summary button with the date-picker range.
const BANNER_SPARK_CONTAINER_ID = 'spark-banner-chat-container';
const BANNER_SPARK_BUTTON_ID = 'spark-banner-analyze-button-id';

const getBannerStorageKey = (chargerId: string) => `spark_banner_dismissed_${chargerId}`;

const readBannerDismissed = (chargerId: string): boolean => {
  try {
    const raw = localStorage.getItem(getBannerStorageKey(chargerId));
    if (!raw) return false;
    return Date.now() - Number(raw) < BANNER_DISMISS_DURATION_MS;
  } catch {
    return false;
  }
};

const writeBannerDismissed = (chargerId: string): void => {
  try {
    localStorage.setItem(getBannerStorageKey(chargerId), String(Date.now()));
  } catch {
    // ignore storage failures
  }
};

export const SparkSessionBanner = ({ charger, onViewChargerHistory }: IProps) => {
  const { t } = useTranslation();
  const { allowSparkBannerMessaging } = useAllowedFeatures();
  const openSparkChat = useOpenSparkChat(BANNER_SPARK_BUTTON_ID);

  const [dismissedChargerId, setDismissedChargerId] = useState<string | null>(null);

  const isBannerDismissed = useMemo(() => {
    if (!charger.id) return true;
    if (dismissedChargerId === charger.id) return true;
    return readBannerDismissed(charger.id);
  }, [charger.id, dismissedChargerId]);

  const { data: bannerData } = useFetchChargerMessageBannersQuery(charger.id, {
    skip: !allowSparkBannerMessaging || isBannerDismissed,
  });

  const sparkDateTimeRange = useMemo(() => {
    if (!bannerData?.lookbackDays) {
      return null;
    }
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - bannerData.lookbackDays);
    return {
      selDates: [startDate, endDate],
      selOption: 'CUSTOM_RANGE',
    };
  }, [bannerData?.lookbackDays]);

  const isBannerVisible = allowSparkBannerMessaging && !!bannerData && !isBannerDismissed;

  useEffect(() => {
    if (isBannerVisible) {
      captureAnalyticsEvent(AnalyticsEvent.SPARK_SESSION_BANNER_VIEWED, {
        chargerId: charger.id,
        severity: bannerData?.severity,
      });
    }
  }, [isBannerVisible, charger.id, bannerData?.severity]);

  const handleCallToAction = useCallback(() => {
    captureAnalyticsEvent(AnalyticsEvent.SPARK_SESSION_BANNER_LEARN_MORE_CLICKED, {
      chargerId: charger.id,
      severity: bannerData?.severity,
    });
    if (bannerData?.callToActionType === SparkBannerCallToAction.CHARGER_HISTORY) {
      onViewChargerHistory();
    } else {
      openSparkChat();
    }
  }, [
    charger.id,
    bannerData?.severity,
    bannerData?.callToActionType,
    onViewChargerHistory,
    openSparkChat,
  ]);

  const handleBannerDismiss = useCallback(() => {
    captureAnalyticsEvent(AnalyticsEvent.SPARK_SESSION_BANNER_DISMISSED, {
      chargerId: charger.id,
      severity: bannerData?.severity,
    });
    writeBannerDismissed(charger.id);
    setDismissedChargerId(charger.id);
  }, [charger.id, bannerData?.severity]);

  if (!isBannerVisible || !bannerData) {
    return null;
  }

  return (
    <div className='mt-6 mb-6 flex w-full min-w-0 max-w-full flex-col'>
      <InlineMessage
        severity={bannerData.severity}
        leadMessage={bannerData.leadMessage}
        supportingMessage={bannerData.supportingMessage}
        leadingIcon={SparkIcon}
        onBtnClick={bannerData.callToActionType ? handleCallToAction : undefined}
        onDismiss={handleBannerDismiss}
        dataTestId='charger-session-banner'
      />
      {/* The widget owns the chat; "Learn more" opens it by clicking this hidden
          button (see useOpenSparkChat). Mounted only once the lookback is known,
          since that window is what the analysis covers. */}
      {sparkDateTimeRange && (
        <div className='hidden'>
          <SparkChatWidget
            charger={charger}
            label={t('learn_more')}
            dateTimeRange={sparkDateTimeRange}
            trigger={BANNER_TRIGGER}
            containerId={BANNER_SPARK_CONTAINER_ID}
            buttonId={BANNER_SPARK_BUTTON_ID}
          />
        </div>
      )}
    </div>
  );
};
